import { MouseEvent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PopupItem, useGetPopupApi } from '@/api/popup';
import ModalPortal from '@/components/common/Modal/ModalPortal/ModalPortal';

import * as S from './style';

const POPUP_HIDE_STORAGE_KEY = 'popup-hide-until';
const SEVEN_DAYS_IN_MS = 7 * 24 * 60 * 60 * 1000;
const DEFAULT_POPUP_POSITION: S.PopupPosition = '좌측상단';

const getPopupStorageKey = (popupPk: number) => `${POPUP_HIDE_STORAGE_KEY}:${popupPk}`;
const getPopupPosition = (popup: PopupItem): S.PopupPosition =>
	popup.popup_exposure_location === '중앙' ||
	popup.popup_exposure_location === '우측상단' ||
	popup.popup_exposure_location === '좌측하단' ||
	popup.popup_exposure_location === '우측하단'
		? popup.popup_exposure_location
		: DEFAULT_POPUP_POSITION;

const isPopupHidden = (popupPk: number) => {
	const hiddenUntil = window.localStorage.getItem(getPopupStorageKey(popupPk));
	if (!hiddenUntil) return false;

	const expiresAt = Number(hiddenUntil);
	if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
		window.localStorage.removeItem(getPopupStorageKey(popupPk));
		return false;
	}

	return true;
};

const PopupLayer = () => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { data } = useGetPopupApi();
	const [dismissedPopupIds, setDismissedPopupIds] = useState<number[]>([]);
	const [popupOrder, setPopupOrder] = useState<number[]>([]);
	const [popupDragOffset, setPopupDragOffset] = useState<Record<number, { x: number; y: number }>>({});
	const [draggingPopupPk, setDraggingPopupPk] = useState<number | null>(null);
	const dragStateRef = useRef<{
		popupPk: number;
		startX: number;
		startY: number;
		originX: number;
		originY: number;
	} | null>(null);

	const handleClose = (popupPk: number) => {
		setDismissedPopupIds((prev) => (prev.includes(popupPk) ? prev : [...prev, popupPk]));
	};

	const handleHideForWeek = (popupPk: number) => {
		window.localStorage.setItem(getPopupStorageKey(popupPk), String(Date.now() + SEVEN_DAYS_IN_MS));
		handleClose(popupPk);
	};

	const handlePopupClick = (popup: PopupItem) => {
		if (!popup.popup_url) return;
		window.open(popup.popup_url, '_blank', 'noopener,noreferrer');
	};

	const visiblePopups =
		data?.popup?.filter((popup) => {
			if (!popup?.popup_pk || !popup.popup_image) return false;
			if (dismissedPopupIds.includes(popup.popup_pk)) return false;
			return !isPopupHidden(popup.popup_pk);
		}) ?? [];
	const shouldRenderPopups = pathname === '/' && visiblePopups.length > 0;

	useEffect(() => {
		const visiblePopupIds = visiblePopups.map((popup) => popup.popup_pk);

		setPopupOrder((prev) => {
			const remainingIds = prev.filter((popupPk) => visiblePopupIds.includes(popupPk));
			const nextIds = visiblePopupIds.filter((popupPk) => !remainingIds.includes(popupPk));
			const nextOrder = [...remainingIds, ...nextIds];

			if (prev.length === nextOrder.length && prev.every((popupPk, index) => popupPk === nextOrder[index])) {
				return prev;
			}

			return nextOrder;
		});
	}, [visiblePopups]);

	const handleBringToFront = (popupPk: number) => {
		setPopupOrder((prev) => {
			const filtered = prev.filter((id) => id !== popupPk);
			return [...filtered, popupPk];
		});
	};

	const handleStartDrag = (event: MouseEvent<HTMLElement>, popupPk: number) => {
		if (event.button !== 0) return;
		handleBringToFront(popupPk);
		const currentOffset = popupDragOffset[popupPk] ?? { x: 0, y: 0 };
		dragStateRef.current = {
			popupPk,
			startX: event.clientX,
			startY: event.clientY,
			originX: currentOffset.x,
			originY: currentOffset.y,
		};
		setDraggingPopupPk(popupPk);
		event.preventDefault();
	};

	useEffect(() => {
		if (!draggingPopupPk) return;

		const handleMouseMove = (event: globalThis.MouseEvent) => {
			if (!dragStateRef.current) return;
			const { popupPk, startX, startY, originX, originY } = dragStateRef.current;

			setPopupDragOffset((prev) => ({
				...prev,
				[popupPk]: {
					x: originX + (event.clientX - startX),
					y: originY + (event.clientY - startY),
				},
			}));
		};

		const handleMouseUp = () => {
			dragStateRef.current = null;
			setDraggingPopupPk(null);
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [draggingPopupPk]);

	useEffect(() => {
		if (!shouldRenderPopups) return;

		const previousOverflow = document.body.style.overflow;
		const previousTouchAction = document.body.style.touchAction;

		document.body.style.overflow = 'hidden';
		document.body.style.touchAction = 'none';

		return () => {
			document.body.style.overflow = previousOverflow;
			document.body.style.touchAction = previousTouchAction;
		};
	}, [shouldRenderPopups]);

	if (!shouldRenderPopups) return null;

	return (
		<ModalPortal>
			<S.PopupLayer>
				<S.PopupMask />
				<S.PopupList $count={visiblePopups.length}>
					{visiblePopups.map((popup, index) => {
						const orderIndex = popupOrder.indexOf(popup.popup_pk);
						const zIndexStack = orderIndex === -1 ? index : orderIndex;
						const popupPosition = getPopupPosition(popup);
						const sameLocationVisibleIds = visiblePopups
							.filter((item) => getPopupPosition(item) === popupPosition)
							.map((item) => item.popup_pk);
						const sameLocationOrderIds = popupOrder.filter((popupPk) => sameLocationVisibleIds.includes(popupPk));
						const fallbackStack = sameLocationVisibleIds.indexOf(popup.popup_pk);
						const stackIndex = sameLocationOrderIds.indexOf(popup.popup_pk);
						const locationStack = stackIndex === -1 ? fallbackStack : stackIndex;

						return (
							<S.PopupCard
								key={popup.popup_pk}
								$position={popupPosition}
								$offsetX={locationStack * 10}
								$offsetY={locationStack * 10}
								$dragX={popupDragOffset[popup.popup_pk]?.x ?? 0}
								$dragY={popupDragOffset[popup.popup_pk]?.y ?? 0}
								$zIndex={100 + zIndexStack}
								onMouseDown={() => handleBringToFront(popup.popup_pk)}
							>
								<S.PopupHeader
									$isDragging={draggingPopupPk === popup.popup_pk}
									onMouseDown={(event) => handleStartDrag(event, popup.popup_pk)}
								>
									<S.PopupTitle>{popup.popup_name}</S.PopupTitle>
								</S.PopupHeader>
								<S.PopupImageButton type="button" onClick={() => handlePopupClick(popup)} disabled={!popup.popup_url}>
									<S.PopupImage src={popup.popup_image} alt={popup.popup_name} />
								</S.PopupImageButton>
								<S.PopupFooter>
									<S.PopupActionButton type="button" onClick={() => handleHideForWeek(popup.popup_pk)}>
										{t('common.popup.hideFor7Days')}
									</S.PopupActionButton>
									<S.PopupCloseButton type="button" onClick={() => handleClose(popup.popup_pk)}>
										{t('common.popup.close')}
									</S.PopupCloseButton>
								</S.PopupFooter>
							</S.PopupCard>
						);
					})}
				</S.PopupList>
			</S.PopupLayer>
		</ModalPortal>
	);
};

export default PopupLayer;
